// @givmo/design-system — when a host's router may take over a real link.
//
// Every destination this system renders is a REAL link: it carries an `href`, so copy-address,
// open-in-a-new-tab and middle-click keep working, and a host that runs no router still navigates.
// A host that DOES run one may take the ordinary left-click and hop in-app instead — but ONLY that
// click, and this predicate is the whole of "only that one".
//
// The four cases it refuses are the ones where intercepting would silently break a link people
// already use: a MODIFIED click (the browser's own open-in-a-new-tab / new-window / download
// gestures), a NON-PRIMARY button (middle-click opens a tab), a link aimed at another target, and a
// default somebody else already prevented.
//
// It lives here rather than in each caller because two of them exist already — the app grid's square
// and the direct-link module gate's door — and a second copy of a rule about which clicks a router
// may swallow is how one of them ends up eating a middle-click.

import type { MouseEvent } from 'react';

/**
 * Whether a click on an anchor may be handled in-app instead of by the browser.
 *
 * `false` means: leave the event alone and let the anchor do what anchors do.
 */
export function shouldInterceptClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  if (event.defaultPrevented) return false;
  // `button` is 0 for the primary button. Middle-click (1) opens a new tab and must reach the browser.
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const target = event.currentTarget.target;
  return target === '' || target === '_self';
}
